import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  Input,
  Select,
} from '@chakra-ui/react';
import { ChatData, MessageType } from 'modules/common/components/Chat/Chat.types';
import { ChangeEvent, Dispatch, MouseEvent, SetStateAction, useRef, useState } from 'react';

type TestChatMessagesProps = {
  chatData: ChatData[];
  setChatData: Dispatch<SetStateAction<ChatData[]>>;
};

const MessagePlaceholders: [MessageType, string[]][] = [
  [MessageType.BOT, ['Bot message']],
  [MessageType.USER, ['User message']],
  [MessageType.BOT_GENERATING, ['start | stop', 'bot message']],
  [MessageType.SUGGESTION, ['Suggestion message 1', 'Suggestion message 2', 'Suggestion message 3']],
  [MessageType.DESIGN, ['e2DesignId', 'userMessage']],
];

export const TestChatMessages = ({ chatData, setChatData }: TestChatMessagesProps) => {
  const [messagesPlaceholders, setMessagesPlaceholders] = useState<string[]>([]);
  const handleRemove = (chat: ChatData) => {
    setChatData((prev) => prev.filter((chatData) => chatData !== chat));
  };
  const formRef = useRef<HTMLFormElement>(null);

  const handleMessageTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    if (!e.target.value) {
      setMessagesPlaceholders([]);
      return;
    }

    const messageType = e.target.value as MessageType;
    const placeholders = MessagePlaceholders.find((data) => data[0] === messageType);

    if (!placeholders?.[1]) {
      setMessagesPlaceholders([]);
      return;
    }

    setMessagesPlaceholders(placeholders[1]);
  };

  const handleAddMessageClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (!formRef?.current) return;
    let type;
    let messages = [];
    const formData = new FormData(formRef.current);
    for (var [key, value] of formData.entries()) {
      if (key === 'type') {
        type = value as MessageType;
      }
      if (key.startsWith('message')) {
        messages.push(value as string);
      }
    }

    if (!type || messages.length === 0) {
      alert('Please select type or fill the input field');
      return;
    }

    const newMessageObj =
      type === MessageType.DESIGN
        ? {
            type: MessageType.DESIGN,
            messages: messages,
            children: (
              <Flex h='200px' alignItems='center' justifyContent='center'>
                Preview
              </Flex>
            ),
            createdAt: Date.now(),
          }
        : {
            type: type as MessageType,
            messages: messages,
            createdAt: Date.now(),
          };

    setChatData((prev) => [...prev, newMessageObj]);
  };

  return (
    <Box w='full'>
      <Accordion allowToggle>
        <AccordionItem>
          <h3>
            <AccordionButton>
              <Box as='span' flex='1' textAlign='left'>
                Messages
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
              <Flex gap='1' flexDir='column'>
                {chatData.map((chat, index) => (
                  <Flex key={index} borderBottom='1px solid #ddd' justifyContent='space-between'>
                    {chat.type}
                    <Box>
                      <Button size='xs' onClick={() => handleRemove(chat)}>
                        X
                      </Button>
                    </Box>
                  </Flex>
                ))}
              </Flex>
            </AccordionPanel>
          </h3>
        </AccordionItem>

        <AccordionItem>
          <h3>
            <AccordionButton>
              <Box as='span' flex='1' textAlign='left'>
                New message
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={4}>
            <Box>
              <form ref={formRef}>
                <FormControl as='fieldset'>
                  <FormHelperText>Type</FormHelperText>
                  <Select placeholder='Select message type' onChange={handleMessageTypeChange} name='type' mt='1'>
                    {MessagePlaceholders.map((data) => (
                      <option value={data[0].toString()} key={data[0].toString()}>
                        {data[0].toString().toUpperCase()}
                      </option>
                    ))}
                  </Select>
                  <FormHelperText>Values</FormHelperText>
                  {messagesPlaceholders.map((placeholder, index) => (
                    <Input key={index} placeholder={placeholder} name={`message-${index}`} mb='2'></Input>
                  ))}

                  <Button mt='2' ml='auto' onClick={handleAddMessageClick}>
                    Add
                  </Button>
                </FormControl>
              </form>
            </Box>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );
};
